import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEO from "../components/SEO";
import { PAGE_SEO, FAQ_SCHEMA } from "../seo-data";
import { openBookingModal } from "../components/BookingModal";
import { 
  PhoneCall, Settings, Rocket, BarChart3, RefreshCw, Sparkles
} from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const HowItWorks = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observerRef.current.unobserve(entry.target);
        }
      });
    }, { threshold: 0.08 });

    document.querySelectorAll('.reveal:not(.visible)').forEach(el => {
      observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  const steps = [
    {
      number: "01",
      icon: <PhoneCall className="w-6 h-6" />,
      title: "AI Growth Planning Call",
      description: "We start with a free strategy call to understand your business, workflows, and pain points. We'll map out exactly which AI systems will have the biggest impact.",
      timeline: "Day 1"
    },
    {
      number: "02",
      icon: <Settings className="w-6 h-6" />,
      title: "We Build Your AI Systems",
      description: "Your dedicated team builds everything — custom voice agents, chatbots, follow-up sequences, CRM integrations, and workflow automations tailored to your business.",
      timeline: "Days 2-14"
    },
    {
      number: "03",
      icon: <Rocket className="w-6 h-6" />,
      title: "Systems Go Live",
      description: "We launch your custom AI systems. Voice agents start answering calls, chatbots engage visitors, and automated follow-up kicks in — all running 24/7.",
      timeline: "Day 15-20"
    },
    {
      number: "04",
      icon: <BarChart3 className="w-6 h-6" />,
      title: "AI Handles Your Pipeline",
      description: "Our AI qualifies leads, books appointments on your calendar, and follows up automatically. Your team focuses on the conversations that close deals.",
      timeline: "Day 20+"
    },
    {
      number: "05",
      icon: <RefreshCw className="w-6 h-6" />,
      title: "We Optimize & Improve",
      description: "Our team tracks every metric — response rates, conversion rates, booking rates — and continuously optimizes your AI systems for better results week over week.",
      timeline: "Ongoing"
    },
    {
      number: "06",
      icon: <Sparkles className="w-6 h-6" />,
      title: "Scale Your Operations",
      description: "As your business grows, we scale your AI systems to match. More channels, more automations, more capacity — without adding headcount.",
      timeline: "When ready"
    }
  ];

  const faqs = [
    {
      q: "How fast will I see results?",
      a: "Most clients go live within 30 days. AI voice agents and chatbots start handling calls and messages immediately after launch, with automated follow-up running 24/7 from day one."
    },
    {
      q: "Do I need to do anything technical?",
      a: "Not a thing. Your dedicated team handles everything — AI buildout, CRM connections, workflow setup, and ongoing management. You don't touch any code or software."
    },
    {
      q: "What if I already have a CRM?",
      a: "Great. We integrate with HubSpot, Salesforce, GoHighLevel, Jobber, and 50+ other tools your team already uses. All leads and data flow in automatically — no double entry."
    },
    {
      q: "What types of businesses do you work with?",
      a: "Any business that handles leads, appointments, customer communication, or repetitive workflows. Service businesses, agencies, e-commerce, healthcare — if you need AI to scale operations, we can build it."
    },
    {
      q: "What if I already have a lot of leads but can't follow up fast enough?",
      a: "That's one of the most common problems we solve. We build AI systems that respond to every lead instantly — by text, voice, and email — so nothing falls through the cracks, even when your team is busy."
    },
    {
      q: "Is there a long-term contract?",
      a: "No long-term contracts. We earn your business month after month with real results. Our clients stay because the AI systems we build drive measurable ROI."
    }
  ];

  return (
    <div className="how-it-works-page">
      <SEO {...PAGE_SEO.howItWorks} schema={FAQ_SCHEMA} />
      {/* Hero */}
      <section className="hiw-hero">
        <div className="hiw-hero-bg"></div>
        <div className="hiw-hero-particles">
          <div className="hiw-particle p1"></div>
          <div className="hiw-particle p2"></div>
          <div className="hiw-particle p3"></div>
        </div>
        <div className="container">
          <div className="hiw-hero-content reveal">
            <div className="eyebrow">The Process</div>
            <h1 data-testid="hiw-hero-title">From Zero to Live AI Systems<br /><span className="gradient-text">in 30 Days or Less</span></h1>
            <p className="hiw-hero-sub">
              Here's exactly how we build, launch, and scale your custom AI systems. 
              No guesswork, no delays — your business just runs smarter.
            </p>
          </div>
        </div>
      </section>

      {/* Timeline Steps */}
      <section className="hiw-steps-section">
        <div className="container">
          <div className="hiw-timeline">
            {steps.map((step, index) => (
              <div key={step.number} className={`hiw-step reveal ${index % 2 === 1 ? 'step-right' : ''}`}>
                <div className="step-connector">
                  <div className="step-line"></div>
                  <div className="step-dot"></div>
                </div>
                <div className="step-card">
                  <div className="step-timeline-badge">{step.timeline}</div>
                  <div className="step-number">{step.number}</div>
                  <div className="step-icon">{step.icon}</div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Timeline Visual */}
      <section className="results-timeline-section">
        <div className="container">
          <div className="results-header reveal">
            <div className="eyebrow">Expected Timeline</div>
            <h2 className="section-h">When You'll See Results</h2>
          </div>
          <div className="results-timeline reveal">
            <div className="timeline-bar">
              <div className="timeline-progress"></div>
            </div>
            <div className="timeline-markers">
              <div className="timeline-marker">
                <div className="marker-dot"></div>
                <div className="marker-content">
                  <span className="marker-day">Day 1</span>
                  <span className="marker-text">Growth planning call</span>
                </div>
              </div>
              <div className="timeline-marker">
                <div className="marker-dot"></div>
                <div className="marker-content">
                  <span className="marker-day">Day 15</span>
                  <span className="marker-text">Ads go live</span>
                </div>
              </div>
              <div className="timeline-marker">
                <div className="marker-dot active"></div>
                <div className="marker-content">
                  <span className="marker-day">Day 30</span>
                  <span className="marker-text">AI systems live</span>
                </div>
              </div>
              <div className="timeline-marker">
                <div className="marker-dot"></div>
                <div className="marker-content">
                  <span className="marker-day">Day 90</span>
                  <span className="marker-text">Fully optimized</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="hiw-faq-section">
        <div className="container">
          <div className="faq-header reveal">
            <div className="eyebrow">FAQ</div>
            <h2 className="section-h">Common Questions</h2>
          </div>
          <div className="faq-content reveal">
            <Accordion type="single" collapsible className="faq-accordion">
              {faqs.map((faq, index) => (
                <AccordionItem key={`faq-${index}`} value={`item-${index}`}>
                  <AccordionTrigger className="faq-question">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent className="faq-answer">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="hiw-cta">
        <div className="container">
          <div className="hiw-cta-inner reveal">
            <h2>Ready to Get Started?</h2>
            <p>Book your free AI Growth Planning Call and see how custom AI can transform your operations in 30 days or less.</p>
            <button onClick={openBookingModal} className="btn-gradient" data-testid="hiw-cta-button">
              Book My AI Growth Planning Call →
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HowItWorks;
